import primaryComponents from "../../../../components/primaryComponents";
import secondaryComponents from "../../../../components/secondaryComponents";
import { useAccountingProfitLoss } from "./useAccountingProfitLoss";
import { formatCurrency } from "../../../../helpers";
import styles from "./styles.module.scss";

const AccountingProfitLoss = () => {
  const {
    startDate,
    endDate,
    reportData,
    isLoadingReport,
    storeList,
    storeHasMultipleCurrency,
    currentStore,
    setDateFilter,
    filterByStoreId,
    fetchProfitAndLossReport,
  } = useAccountingProfitLoss();

  const currencyCode = reportData?.currency_code || "NGN";
  const formatAmount = (value) =>
    formatCurrency({ value: value ?? 0, currencyCode });

  return (
    <>
      <div className="d-flex justify-content-between p-3">
        <div style={{ width: "200px" }}>
          <primaryComponents.SelectField
            options={
              storeHasMultipleCurrency
                ? [...storeList]
                : [
                    {
                      name: "All stores",
                      id: "ALL_STORES",
                    },
                    ...storeList,
                  ]
            }
            idKey="id"
            nameKey="name"
            onChange={(value) => {
              filterByStoreId(value);
            }}
            value={
              storeList?.find(
                (storeItem) => `${storeItem?.id}` === `${currentStore}`,
              )?.name || "All stores"
            }
            floatError={false}
            classNames="store-select"
          />
        </div>
        <div className="d-flex align-items-end" style={{ gap: "12px" }}>
          <div>
            <label htmlFor="">Date Range</label>
            <primaryComponents.DateDefinedRangeInput
              startDate={startDate}
              endDate={endDate}
              onChange={setDateFilter}
              showPlaceholder={true}
            />
          </div>
          <div>
            <primaryComponents.Button
              classNames="btn btn--primary"
              onClick={fetchProfitAndLossReport}
              isLoading={isLoadingReport}
              loadingText="Generating..."
            >
              Generate Report
            </primaryComponents.Button>
          </div>
        </div>
      </div>

      <secondaryComponents.LoaderHelper
        isLoading={isLoadingReport}
        classNames="mt-4"
      >
        {!reportData ? (
          <div className={styles.emptyState}>
            Set a date range and click Generate Report to view your Profit and
            Loss Statement.
          </div>
        ) : (
          <div className={styles.reportCard}>
            {/* Table */}
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Account</th>
                  <th className={styles.reportTable__totalCol}>Total</th>
                </tr>
              </thead>
              <tbody>
                {/* ── Operating Income ─────────────────────────────────── */}
                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>Operating Income</td>
                </tr>

                {/* Sales sub-header */}
                <tr className={styles.subHeader}>
                  <td colSpan={2}>Sales</td>
                </tr>
                {(reportData?.income_accounts?.sales_revenue || []).map(
                  (item, idx) => (
                    <tr key={idx} className={styles.lineItem}>
                      <td className={styles.lineItem__name}>
                        <span className={styles.lineItem__code}>
                          {item.account_code}{" "}
                        </span>
                        {item.account_name}
                      </td>
                      <td className={styles.lineItem__amount}>
                        {formatAmount(item.net_balance)}
                      </td>
                    </tr>
                  ),
                )}

                {/* Contra sub-header */}
                <tr className={styles.subHeader}>
                  <td colSpan={2}>Contra</td>
                </tr>
                {[
                  ...(reportData?.income_accounts?.returns_outward || []),
                  ...(reportData?.income_accounts?.discount_allowed || []),
                ].map((item, idx) => (
                  <tr key={idx} className={styles.lineItem}>
                    <td className={styles.lineItem__name}>
                      <span className={styles.lineItem__code}>
                        {item.account_code}{" "}
                      </span>
                      {item.account_name}
                    </td>
                    <td
                      className={`${styles.lineItem__amount} ${styles.lineItem__negative}`}
                    >
                      {formatAmount(item.net_balance)}
                    </td>
                  </tr>
                ))}

                <tr className={styles.subtotalRow}>
                  <td>Total Operating Income</td>
                  <td>{formatAmount(reportData?.total_operating_income)}</td>
                </tr>
                <tr className={styles.thinDivider}>
                  <td colSpan={2} />
                </tr>

                {/* ── Cost of Goods Sold ───────────────────────────────── */}
                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>Cost of Goods Sold</td>
                </tr>
                {(reportData?.expense_accounts?.cost_of_goods_sold || []).map(
                  (item, idx) => (
                    <tr key={idx} className={styles.lineItem}>
                      <td className={styles.lineItem__name}>
                        <span className={styles.lineItem__code}>
                          {item.account_code}{" "}
                        </span>
                        {item.account_name}
                      </td>
                      <td className={styles.lineItem__amount}>
                        {formatAmount(item.net_balance)}
                      </td>
                    </tr>
                  ),
                )}
                <tr className={styles.subtotalRow}>
                  <td>Total Cost of Goods Sold</td>
                  <td>{formatAmount(reportData?.total_cogs)}</td>
                </tr>
                <tr className={styles.thinDivider}>
                  <td colSpan={2} />
                </tr>

                {/* Gross Profit */}
                <tr className={styles.highlightRow}>
                  <td>Gross Profit</td>
                  <td>{formatAmount(reportData?.gross_profit)}</td>
                </tr>
                <tr className={styles.thickDivider}>
                  <td colSpan={2} />
                </tr>

                {/* ── Operating Expenses ───────────────────────────────── */}
                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>Operating Expenses</td>
                </tr>
                {[
                  ...(reportData?.expense_accounts?.operating_expenses || []),
                  ...(reportData?.expense_accounts?.depreciation || []),
                  ...(reportData?.expense_accounts?.amortization || []),
                ].map((item, idx) => (
                  <tr key={idx} className={styles.lineItem}>
                    <td className={styles.lineItem__name}>
                      <span className={styles.lineItem__code}>
                        {item.account_code}{" "}
                      </span>
                      {item.account_name}
                    </td>
                    <td className={styles.lineItem__amount}>
                      {formatAmount(item.net_balance)}
                    </td>
                  </tr>
                ))}
                <tr className={styles.subtotalRow}>
                  <td>Total Operating Expense</td>
                  <td>{formatAmount(reportData?.total_operating_expenses)}</td>
                </tr>
                <tr className={styles.thinDivider}>
                  <td colSpan={2} />
                </tr>

                {/* Operating Profit */}
                <tr className={styles.highlightRow}>
                  <td>Operating Profit</td>
                  <td>{formatAmount(reportData?.operating_profit)}</td>
                </tr>
                <tr className={styles.thickDivider}>
                  <td colSpan={2} />
                </tr>

                {/* ── Non Operating Income ─────────────────────────────── */}
                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>Non Operating Income</td>
                </tr>
                {(reportData?.income_accounts?.other_income || []).map(
                  (item, idx) => (
                    <tr key={idx} className={styles.lineItem}>
                      <td className={styles.lineItem__name}>
                        <span className={styles.lineItem__code}>
                          {item.account_code}{" "}
                        </span>
                        {item.account_name}
                      </td>
                      <td className={styles.lineItem__amount}>
                        {formatAmount(item.net_balance)}
                      </td>
                    </tr>
                  ),
                )}
                <tr className={styles.highlightRow}>
                  <td>Profit with Non Operating Income</td>
                  <td>
                    {formatAmount(reportData?.profit_with_non_operating_income)}
                  </td>
                </tr>

                {/* ── Non Operating Expense ────────────────────────────── */}
                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>Non Operating Expense</td>
                </tr>
                {[
                  ...(reportData?.expense_accounts?.other_expenses || []),
                  ...(reportData?.expense_accounts?.interest_expenses || []),
                ].map((item, idx) => (
                  <tr key={idx} className={styles.lineItem}>
                    <td className={styles.lineItem__name}>
                      <span className={styles.lineItem__code}>
                        {item.account_code}{" "}
                      </span>
                      {item.account_name}
                    </td>
                    <td className={styles.lineItem__amount}>
                      {formatAmount(item.net_balance)}
                    </td>
                  </tr>
                ))}
                <tr className={styles.subtotalRow}>
                  <td>Total Non Operating Expense</td>
                  <td>{formatAmount(reportData?.non_operating_expenses)}</td>
                </tr>
                <tr className={styles.thickDivider}>
                  <td colSpan={2} />
                </tr>

                {/* Profit Before Tax */}
                <tr className={styles.highlightRow}>
                  <td>Profit Before Tax</td>
                  <td>{formatAmount(reportData?.profit_before_tax)}</td>
                </tr>
                <tr className={styles.thickDivider}>
                  <td colSpan={2} />
                </tr>

                {/* ── Tax Expense ──────────────────────────────────────── */}
                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>Tax Expense</td>
                </tr>
                {(reportData?.expense_accounts?.income_tax_expenses || []).map(
                  (item, idx) => (
                    <tr key={idx} className={styles.lineItem}>
                      <td className={styles.lineItem__name}>
                        <span className={styles.lineItem__code}>
                          {item.account_code}{" "}
                        </span>
                        {item.account_name}
                      </td>
                      <td className={styles.lineItem__amount}>
                        {formatAmount(item.net_balance)}
                      </td>
                    </tr>
                  ),
                )}
                <tr className={styles.subtotalRow}>
                  <td>Total Income Tax</td>
                  <td>{formatAmount(reportData?.total_income_tax_expenses)}</td>
                </tr>

                {/* Net Profit / Loss */}
                <tr className={styles.netProfitRow}>
                  <td>Net Profit / Loss</td>
                  <td>{formatAmount(reportData?.net_profit)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </secondaryComponents.LoaderHelper>
    </>
  );
};

export default AccountingProfitLoss;
